import React, { useEffect, useMemo } from 'react';
import {
    TransferReceiptOrderSaleInputQuery,
    useTransferReceiptOrderSaleInputLazyQuery,
} from '../../../../../../../../services';
import { RhSingleAutocomplete } from '../../../../../../../dum';
import { FROM_ACCOUNT, TO_ACCOUNT, ITransfer } from '../../transfer-form';
import { useFormContext, useWatch } from 'react-hook-form';
import {
    AMOUNT,
    INITIAL_AMOUNT,
    INITIAL_ORDER_SALE_ID,
    SUGGESTED_AMOUNT,
    TOTAL,
    TRANSFER_RECEIPTS,
} from '../transfer-receipt-columns';
import { useCalculateTransferReceiptsTotal } from '../../use-calculate-transfer-receipt-total';
import {
    FIELD_LABELS,
    formatFloat,
    round,
    shortLabel,
} from '../../../../../../../../helpers';

const ARRAY_NAME: keyof ITransfer = 'transfer_receipts';
const ORDER_SALE_NAME: keyof ITransfer['transfer_receipts'][number] =
    'order_sale';

interface IResourceInput {
    index: number;
}

export function OrderSalesInput({ index }: IResourceInput) {
    const [query, { data }] = useTransferReceiptOrderSaleInputLazyQuery();
    const { calculateTransferReceiptsTotal } =
        useCalculateTransferReceiptsTotal();
    const INPUT_NAME = `${ARRAY_NAME}.${index}.${ORDER_SALE_NAME}`;
    const fromAccount = useWatch({
        name: FROM_ACCOUNT,
    }) as ITransfer['from_account'];
    const toAccount = useWatch({
        name: TO_ACCOUNT,
    }) as ITransfer['to_account'];
    // Formality of the participating OWN account (the sale's counterpart is a
    // client, so the own account is the "to" side on a client payment; the
    // generic find also covers adjustments). Used to keep only receipt types
    // that match it. undefined → no own account / unknown → do not filter.
    const ownIsInformal = fromAccount?.is_own
        ? fromAccount.is_informal_account
        : toAccount?.is_own
        ? toAccount.is_informal_account
        : undefined;
    const initialOrderSaleId = useWatch({
        name: `${TRANSFER_RECEIPTS}.${index}.${INITIAL_ORDER_SALE_ID}`,
    }) as ITransfer['transfer_receipts'][number]['initial_order_sale_id'];
    const rx = useFormContext();

    useEffect(() => {
        if (fromAccount?.is_client) {
            void query({
                variables: {
                    account_id: fromAccount.id,
                },
            });
        }
    }, [query, fromAccount]);

    const sales = useMemo(() => {
        const rawSales: TransferReceiptOrderSaleInputQuery['getOrderSales'] =
            data?.getOrderSales || [];
        // Informal/formal rule: only offer sales whose receipt-type formality
        // matches the participating own account. Skipped when there is no own
        // account or its flag is unknown.
        const eligibleSales =
            typeof ownIsInformal === 'boolean'
                ? rawSales.filter(
                      (sale) =>
                          !!sale.receipt_type?.is_informal_receipt ===
                          ownIsInformal,
                  )
                : rawSales;
        // Append the net total (iva incluido, minus returns) to the visible label
        // so the user can weigh each sale's size without opening it.
        // `option_label` is a display-only field; selection/persistence still
        // keys off `id`.
        return eligibleSales.map((sale) => ({
            ...sale,
            option_label: `${sale.compound_order_code} ($${formatFloat(
                sale.net_total_with_tax || 0,
            )})`,
        }));
    }, [data, ownIsInformal]);

    return (
        <RhSingleAutocomplete
            itemText={'option_label'}
            itemValue={'id'}
            options={sales}
            highlightItemValue={initialOrderSaleId ?? undefined}
            name={INPUT_NAME}
            label={shortLabel(FIELD_LABELS.order_sale_id)}
            disableGutters
            returnObject
            onChange={(val: typeof sales[number]) => {
                rx.setValue(
                    `${TRANSFER_RECEIPTS}.${index}.${TOTAL}`,
                    val.net_total_with_tax || 0,
                    {
                        shouldValidate: true,
                    },
                );

                // Remaining balance still owed on this sale = its net total
                // (after returns) minus whatever other transfers already
                // allocated to it.
                const suggested = round(
                    (val.net_total_with_tax || 0) -
                        (val.transfer_receipts_total || 0),
                );
                rx.setValue(
                    `${TRANSFER_RECEIPTS}.${index}.${SUGGESTED_AMOUNT}`,
                    suggested,
                    { shouldValidate: false },
                );

                const isEditing = !!rx.getValues('id');

                if (
                    initialOrderSaleId != null &&
                    val.id === initialOrderSaleId
                ) {
                    // Re-selected the sale this receipt was loaded with → restore
                    // the amount it had at form init (not the remaining balance).
                    const initialAmount = rx.getValues(
                        `${TRANSFER_RECEIPTS}.${index}.${INITIAL_AMOUNT}`,
                    );
                    rx.setValue(
                        `${TRANSFER_RECEIPTS}.${index}.${AMOUNT}`,
                        initialAmount ?? 0,
                        { shouldValidate: true },
                    );
                } else if (!isEditing) {
                    // Brand-new transfer: pre-fill the remaining balance to save
                    // typing. (While editing a different sale, we leave the
                    // existing amount untouched and only offer the hint.)
                    rx.setValue(
                        `${TRANSFER_RECEIPTS}.${index}.${AMOUNT}`,
                        suggested,
                        { shouldValidate: true },
                    );
                }

                calculateTransferReceiptsTotal({ index });
            }}
        />
    );
}
